/**
 * KPI Calculation Service
 * Calculates and stores Key Performance Indicators for swine production
 * 
 * @path Users/{userId}/KPIMetrics/{date}
 */

import firebase from "firebase/compat/app";
import app from "../firebase";

const db = firebase.firestore(app);

/**
 * Calculates all KPIs for a given time period
 * @param {string} userId - The unique identifier for the user
 * @param {Object} data - Production data for calculations
 * @param {Array} data.services - Array of service/insemination records
 * @param {Array} data.farrowings - Array of farrowing/birth records
 * @param {Array} data.pregnancyChecks - Array of pregnancy check records
 * @param {Array} data.returnToEstrus - Array of return to estrus records
 * @param {Array} data.weanings - Array of weaning records
 * @param {Object} config - ERP configuration (gestation length, lactation days, etc.)
 * @param {string} periodStart - ISO date string for period start
 * @param {string} periodEnd - ISO date string for period end
 * @param {string} periodType - "daily" | "weekly" | "monthly" | "yearly"
 * @returns {Promise<Object>} Promise resolving to calculated KPI object
 */
export async function calculateKPIs(userId, data, config, periodStart, periodEnd, periodType) {
	if (!userId) {
		throw new Error("User ID is required");
	}

	if (!data || !config) {
		throw new Error("Data and config are required");
	}

	const {
		services = [],
		farrowings = [],
		pregnancyChecks = [],
		returnToEstrus = [],
		weanings = [],
	} = data;

	const {
		gestationLength = 114,
		lactationWeaningAge = 28,
	} = config;

	// Helper function to get timestamp from record
	const getRecordTimestamp = (record) => {
		if (record.t) {
			return typeof record.t === "number" ? record.t : parseInt(record.t);
		}
		if (record.StartDate) {
			return new Date(record.StartDate).getTime();
		}
		return 0;
	};

	// Filter records within period
	const periodStartTime = new Date(periodStart).getTime();
	const periodEndTime = new Date(periodEnd).getTime();

	const filterByPeriod = (records) => {
		return records.filter((record) => {
			const timestamp = getRecordTimestamp(record);
			return timestamp >= periodStartTime && timestamp <= periodEndTime;
		});
	};

	const periodServices = filterByPeriod(services.filter((s) => !s.Archived));
	const periodFarrowings = filterByPeriod(farrowings.filter((f) => !f.Archived));
	const periodPregnancyChecks = filterByPeriod(pregnancyChecks.filter((pc) => !pc.Archived));
	const periodReturns = filterByPeriod(returnToEstrus.filter((r) => !r.Archived));
	const periodWeanings = filterByPeriod(weanings.filter((w) => !w.Archived));

	// Calculate Core KPIs

	// Farrowing rate = farrowings ÷ services × 100
	const farrowingRate = periodServices.length > 0
		? (periodFarrowings.length / periodServices.length) * 100
		: null;

	// Conception rate = confirmed pregnant ÷ services × 100
	const confirmedPregnant = periodPregnancyChecks.filter((pc) => pc.result === "pregnant").length;
	const conceptionRate = periodServices.length > 0
		? (confirmedPregnant / periodServices.length) * 100
		: null;

	// Repeat-service rate = returns to estrus ÷ services × 100
	const repeatServiceRate = periodServices.length > 0
		? (periodReturns.length / periodServices.length) * 100
		: null;

	// Services per conception = services ÷ confirmed pregnancies
	const servicesPerConception = confirmedPregnant > 0
		? periodServices.length / confirmedPregnant
		: null;

	// Litters per animal per year - from recorded farrowing interval
	// Simplified calculation: (365 / average_days_between_farrowings)
	// For more accurate calculation, need historical data
	let littersPerAnimalPerYear = null;
	if (periodFarrowings.length > 1) {
		// Calculate average interval between farrowings
		const sortedFarrowings = [...periodFarrowings].sort((a, b) => {
			const timeA = getRecordTimestamp(a);
			const timeB = getRecordTimestamp(b);
			return timeA - timeB;
		});

		let totalInterval = 0;
		let intervalCount = 0;
		for (let i = 1; i < sortedFarrowings.length; i++) {
			const interval = getRecordTimestamp(sortedFarrowings[i]) - getRecordTimestamp(sortedFarrowings[i - 1]);
			const daysInterval = interval / (1000 * 60 * 60 * 24);
			totalInterval += daysInterval;
			intervalCount++;
		}

		if (intervalCount > 0) {
			const avgInterval = totalInterval / intervalCount;
			littersPerAnimalPerYear = 365 / avgInterval;
		}
	} else if (periodFarrowings.length === 1) {
		// Use theoretical calculation based on gestation + lactation + WEI
		const weanToEstrusInterval = config.weanToEstrusInterval || 5;
		const theoreticalInterval = gestationLength + lactationWeaningAge + weanToEstrusInterval;
		littersPerAnimalPerYear = 365 / theoreticalInterval;
	}

	// Animals weaned per year = animals weaned ÷ average breeding females
	const totalPigsWeaned = periodWeanings.reduce((sum, w) => sum + (w.countWeaned || 0), 0);
	// Note: For accurate PSY, we need the average number of breeding females in the period
	// This is a simplified calculation - in production, you'd need to track active breeding females
	const averageBreedingFemales = periodServices.length > 0
		? periodServices.length / (periodServices.length / 52) // Approximate
		: 1;
	const animalsWeanedPerYear = averageBreedingFemales > 0
		? (totalPigsWeaned / averageBreedingFemales) * (365 / ((periodEndTime - periodStartTime) / (1000 * 60 * 60 * 24)))
		: null;

	// Non-productive days (NPD) = 365 − [LSY × (gestation days + lactation days)]
	const nonProductiveDays = littersPerAnimalPerYear !== null
		? 365 - (littersPerAnimalPerYear * (gestationLength + lactationWeaningAge))
		: null;

	// Pre-weaning mortality (PWM) = 1 − (weaned ÷ born alive)
	const totalBornAlive = periodFarrowings.reduce((sum, f) => sum + (f.Living || 0), 0);
	const preWeaningMortality = totalBornAlive > 0
		? (1 - (totalPigsWeaned / totalBornAlive)) * 100
		: null;

	// Stillbirth rate = stillborn ÷ total born
	const totalStillborn = periodFarrowings.reduce((sum, f) => sum + (f.Dead || 0), 0);
	const totalBorn = totalBornAlive + totalStillborn;
	const stillbirthRate = totalBorn > 0
		? (totalStillborn / totalBorn) * 100
		: null;

	// Additional Metrics
	const totalMummies = periodFarrowings.reduce((sum, f) => sum + (f.Mummified || 0), 0);
	const averageMummiesPerLitter = periodFarrowings.length > 0
		? totalMummies / periodFarrowings.length
		: null;

	const totalBirthWeight = periodFarrowings.reduce((sum, f) => sum + (f.AverageLitterWeight || 0), 0);
	const averageBirthWeight = periodFarrowings.length > 0
		? totalBirthWeight / periodFarrowings.length
		: null;

	const totalWeaningAge = periodWeanings.reduce((sum, w) => sum + (w.weaningAge || 0), 0);
	const averageWeaningAge = periodWeanings.length > 0
		? totalWeaningAge / periodWeanings.length
		: null;

	const totalWeaningWeight = periodWeanings.reduce((sum, w) => sum + (w.weaningWeight || 0), 0);
	const averageWeaningWeight = periodWeanings.length > 0
		? totalWeaningWeight / periodWeanings.length
		: null;

	// Parity structure - would need animal data to calculate accurately
	// This is a placeholder - in production, you'd need to track parity per animal
	// TODO [PRIORITY: MEDIUM] Calculate parityDistribution from animal data
	// Requires: Query animals collection, group by parity field, calculate percentages
	const parityDistribution = null;

	// Build KPI result object
	const kpiResult = {
		date: periodStart.split("T")[0], // YYYY-MM-DD format
		calculatedAt: firebase.firestore.FieldValue.serverTimestamp(),
		periodStart,
		periodEnd,
		periodType,
	};

	// Add calculated KPIs (only include if they have values)
	if (farrowingRate !== null && !isNaN(farrowingRate)) {
		kpiResult.farrowingRate = Math.round(farrowingRate * 100) / 100;
	}

	if (conceptionRate !== null && !isNaN(conceptionRate)) {
		kpiResult.conceptionRate = Math.round(conceptionRate * 100) / 100;
	}

	if (repeatServiceRate !== null && !isNaN(repeatServiceRate)) {
		kpiResult.repeatServiceRate = Math.round(repeatServiceRate * 100) / 100;
	}

	if (servicesPerConception !== null && !isNaN(servicesPerConception)) {
		kpiResult.servicesPerConception = Math.round(servicesPerConception * 100) / 100;
	}

	if (littersPerAnimalPerYear !== null && !isNaN(littersPerAnimalPerYear)) {
		kpiResult.littersPerAnimalPerYear = Math.round(littersPerAnimalPerYear * 100) / 100;
	}

	if (animalsWeanedPerYear !== null && !isNaN(animalsWeanedPerYear)) {
		kpiResult.animalsWeanedPerYear = Math.round(animalsWeanedPerYear * 100) / 100;
	}

	if (nonProductiveDays !== null && !isNaN(nonProductiveDays)) {
		kpiResult.nonProductiveDays = Math.round(nonProductiveDays * 100) / 100;
	}

	if (preWeaningMortality !== null && !isNaN(preWeaningMortality)) {
		kpiResult.preWeaningMortality = Math.round(preWeaningMortality * 100) / 100;
	}

	if (stillbirthRate !== null && !isNaN(stillbirthRate)) {
		kpiResult.stillbirthRate = Math.round(stillbirthRate * 100) / 100;
	}

	if (averageMummiesPerLitter !== null && !isNaN(averageMummiesPerLitter)) {
		kpiResult.averageMummiesPerLitter = Math.round(averageMummiesPerLitter * 100) / 100;
	}

	if (averageBirthWeight !== null && !isNaN(averageBirthWeight)) {
		kpiResult.averageBirthWeight = Math.round(averageBirthWeight * 100) / 100;
	}

	if (averageWeaningAge !== null && !isNaN(averageWeaningAge)) {
		kpiResult.averageWeaningAge = Math.round(averageWeaningAge * 100) / 100;
	}

	if (averageWeaningWeight !== null && !isNaN(averageWeaningWeight)) {
		kpiResult.averageWeaningWeight = Math.round(averageWeaningWeight * 100) / 100;
	}

	if (parityDistribution !== null) {
		kpiResult.parityDistribution = parityDistribution;
	}

	return kpiResult;
}

/**
 * Stores calculated KPI metrics in Firestore
 * @param {string} userId - The unique identifier for the user
 * @param {Object} kpiMetrics - Calculated KPI metrics object
 * @returns {Promise<void>}
 * @path Users/{userId}/KPIMetrics/{date}
 */
export async function storeKPIMetrics(userId, kpiMetrics) {
	if (!userId) {
		throw new Error("User ID is required");
	}

	if (!kpiMetrics || !kpiMetrics.date) {
		throw new Error("KPI metrics with date are required");
	}

	try {
		await db
			.collection("Users")
			.doc(userId)
			.collection("KPIMetrics")
			.doc(kpiMetrics.date)
			.set(kpiMetrics, { merge: true });

		console.log("[kpiService][storeKPIMetrics] - KPI metrics stored successfully");
	} catch (error) {
		console.error("[kpiService][storeKPIMetrics] - Error:", error);
		throw error;
	}
}

/**
 * Gets stored KPI metrics for a specific date or date range
 * @param {string} userId - The unique identifier for the user
 * @param {Object} options - Query options
 * @param {string} options.date - Specific date (YYYY-MM-DD) to retrieve
 * @param {string} options.startDate - Start date for range
 * @param {string} options.endDate - End date for range
 * @param {string} options.periodType - Filter by period type
 * @returns {Promise<Array>} Promise resolving to array of KPI metric records
 * @path Users/{userId}/KPIMetrics
 */
export async function getKPIMetrics(userId, options = {}) {
	if (!userId) {
		throw new Error("User ID is required");
	}

	try {
		let query = db
			.collection("Users")
			.doc(userId)
			.collection("KPIMetrics");

		// Filter by specific date if provided
		if (options.date) {
			const docSnap = await query.doc(options.date).get();
			if (docSnap.exists) {
				return [docSnap.data()];
			}
			return [];
		}

		// Filter by date range if provided
		if (options.startDate) {
			query = query.where("date", ">=", options.startDate);
		}

		if (options.endDate) {
			query = query.where("date", "<=", options.endDate);
		}

		// Filter by period type if provided
		if (options.periodType) {
			query = query.where("periodType", "==", options.periodType);
		}

		const snapshot = await query.orderBy("date", "desc").get();

		return snapshot.docs.map((doc) => doc.data());
	} catch (error) {
		console.error("[kpiService][getKPIMetrics] - Error:", error);
		throw error;
	}
}

/**
 * Gets KPI trends over time
 * @param {string} userId - The unique identifier for the user
 * @param {string} kpiName - Name of KPI to track (e.g., "farrowingRate")
 * @param {Object} options - Query options
 * @param {string} options.startDate - Start date for trend
 * @param {string} options.endDate - End date for trend
 * @param {string} options.periodType - Period type to use for trend
 * @returns {Promise<Array>} Promise resolving to array of trend data points
 */
export async function getKPITrends(userId, kpiName, options = {}) {
	if (!userId || !kpiName) {
		throw new Error("User ID and KPI name are required");
	}

	try {
		const metrics = await getKPIMetrics(userId, {
			startDate: options.startDate,
			endDate: options.endDate,
			periodType: options.periodType,
		});

		return metrics
			.filter((metric) => metric[kpiName] !== undefined && metric[kpiName] !== null)
			.map((metric) => ({
				date: metric.date,
				value: metric[kpiName],
				periodType: metric.periodType,
			}))
			.sort((a, b) => a.date.localeCompare(b.date));
	} catch (error) {
		console.error("[kpiService][getKPITrends] - Error:", error);
		throw error;
	}
}

/**
 * Calculates and stores KPIs for a given period
 * Convenience function that combines calculation and storage
 * @param {string} userId - The unique identifier for the user
 * @param {Object} data - Production data
 * @param {Object} config - ERP configuration
 * @param {string} periodStart - ISO date string
 * @param {string} periodEnd - ISO date string
 * @param {string} periodType - Period type
 * @returns {Promise<Object>} Promise resolving to stored KPI metrics
 */
export async function calculateAndStoreKPIs(userId, data, config, periodStart, periodEnd, periodType) {
	if (!userId) {
		throw new Error("User ID is required");
	}

	try {
		const kpiMetrics = await calculateKPIs(userId, data, config, periodStart, periodEnd, periodType);
		await storeKPIMetrics(userId, kpiMetrics);
		return kpiMetrics;
	} catch (error) {
		console.error("[kpiService][calculateAndStoreKPIs] - Error:", error);
		throw error;
	}
}

